import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import type { RequestOptions } from "../../api/client";
import { ApiError } from "../../api/errors";
import { useIntentKey } from "../../lib/useIntentKey";
import { useStaffSession } from "../../auth/guards";
import { Banner, Button, Dialog, Input, Money, RefId, StatusPill } from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { compareDecimal } from "../../lib/money";

// Pieces every staff decision form shares: the refusal renderer, the
// dual-control progress block, the step-up note, the money confirmation
// and the id lookup. The console is an ops tool: dense, plain, and exact.

/** 0036's per-case constant, reused by 0063 (0063:1026, 1147): more than this needs two distinct reviewers. */
export const DUAL_CONTROL_THRESHOLD = "100";

/** Whether an amount needs a second, distinct reviewer under 0036/0063 (strictly more than 100). */
export function needsTwoReviewers(amount: string): boolean {
  try {
    return compareDecimal(amount, DUAL_CONTROL_THRESHOLD) > 0;
  } catch {
    return false;
  }
}

export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : "—";
}

/** Timestamps from raw timestamptz JSON (not _rfc3339) or _rfc3339: both parse. "-infinity" is shown as such. */
export function when(ts: string | null | undefined): string {
  if (!ts) return "—";
  if (ts === "-infinity" || ts === "infinity") return ts;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : formatLocal(d);
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className="mono break-all">{children}</span>;
}

/** An amount that may lack a currency (a transfer reversal row names none, provider_operations.go:106-109). */
export function Amount({ value, currency }: { value: string | null | undefined; currency: string | null | undefined }) {
  if (value == null) return <>—</>;
  if (!currency) return <span className="mono">{value} (currency not reported)</span>;
  return <Money value={value} currency={currency} />;
}

// ---------------------------------------------------------------------------
// Refusals

/**
 * Staff wording for the refusal codes the internal routes answer. Codes
 * come from the SQL (_campaign_reject in 0061/0063, the outcome `reason`
 * of review_*_version) and the Go handlers' own 400 bodies.
 */
const STAFF_CODE_MESSAGE: Record<string, string> = {
  // Review (0019, 0035, 0061).
  regulated_review_required: "This version targets regulated categories. Confirm each one to approve it.",
  category_not_cleared: "A category this version targets isn't cleared for serving yet, so it can't be approved.",
  domain_control_not_verified: "The destination's domain control isn't verified, so this creative can't be approved.",
  version_withdrawn: "The advertiser withdrew or replaced this version, so nothing was reviewed.",
  wrong_status: "This version isn't waiting for review any more.",
  invalid_transition: "This version isn't waiting for review any more.",
  appeal_same_reviewer: "You made the rejection this appeal contests, so a different reviewer has to decide it.",
  appeal_not_pending: "This appeal has already been decided or withdrawn.",
  appeal_not_found: "That appeal doesn't exist.",
  appeal_grant_refused: "The version's approval refused the grant, so nothing changed.",
  decision_reason_required: "Give a reason (1–2,000 characters).",
  decision_must_be_approve_or_reject: "Choose approve or reject.",
  decision_must_be_grant_or_deny: "Choose grant or deny.",
  invalid_decision: "That decision isn't one this command takes.",
  // Disputes (0063 decide_statement_line_dispute).
  dispute_not_found: "That dispute doesn't exist.",
  dispute_already_resolved: "This dispute has already been resolved.",
  reviewer_already_voted: "You've already voted on this dispute. A second, different reviewer has to confirm it.",
  credit_amount_not_applicable: "A credit amount only applies to upholding a publisher dispute that's tied to a delivery.",
  credit_amount_required: "Enter the credit to grant the publisher.",
  invalid_credit_amount: "The credit has more than 10 decimal places.",
  credit_exceeds_share: "That credit is more than the publisher's remaining share of this delivery.",
  credit_amount_mismatch: "The first reviewer upheld a different credit. Every uphold vote has to agree on the same amount.",
  publisher_share_claimed:
    "A payout has already claimed the publisher's share of this delivery, so it can't be reversed here. Deny, or wait for the payout to fail; recovery is a debt case.",
  reservation_not_disputable: "The delivery is no longer in a state an uphold can reverse.",
  reviewer_window_cap_exceeded:
    "Your uphold votes over the last 30 days would pass the 5,000 cap with this one. Another reviewer has to take this dispute.",
  // Generic and transport.
  request_rejected: "The server refused this request.",
  insufficient_role: "This account doesn't hold the platform reviewer role.",
  mfa_step_up_required: "This decision needs a fresh verification. It was not submitted.",
  idempotency_key_mismatch: "The request carried two different references. Nothing was done. Try again.",
  idempotency_key_reused: "This retry doesn't match the request first sent under the same reference, so nothing new was done.",
  concurrent_update_retry: "Someone else changed this at the same moment. Nothing was saved. Try again.",
  case_not_found: "That case doesn't exist.",
  invalid_case_id: "That isn't a valid case id.",
  unknown_receivable_kind: "Choose advertiser receivable or publisher debt.",
  reason_required: "Give a reason.",
  reason_and_idempotency_key_required: "Give a reason.",
  kind_required: "Give the evidence a kind.",
  grounds_required: "State the grounds for the appeal.",
  scope_type_scope_id_reason_required: "Choose a scope, give its id and a reason.",
  invalid_effective_at: "The effective time isn't a valid date and time.",
  client_version_suspension:
    "That's a client-version suspension. Reinstate the entry from the client matrix instead, which records the evidence.",
  client_name_and_version_required: "Enter the client name and version.",
  evidence_class_must_be_independently_verified_or_publisher_attested: "Choose an evidence class.",
  status_must_be_validated_ordinary_suspended_or_safety_suspended: "Choose a status.",
  decision_must_be_confirmed_failed_acknowledged_or_double_payment: "Choose a decision.",
  invalid_body: "The request couldn't be read. Try again.",
  invalid_json: "The request couldn't be read. Try again.",
  internal_error: "The server hit an internal error. Check the current state before retrying.",
  // Migration 0068 (G-53): typed refusals for every internal write —
  // internal_commands.go's writeInternalError, both the new vads_reject
  // codes and the classified legacy "rejected: ..." messages.
  actor_required: "That command needs an acting reviewer, which wasn't recorded. Try again or contact engineering.",
  provider_operation_not_found: "We couldn't find that provider operation.",
  // The SQL's actual raised code (client_matrix.go's not-found checks,
  // 0068:297 and 0069:568) is "approved_client_matrix_entry_not_found";
  // this shorter alias is kept too in case an older server build or a
  // hand-built request still sends it.
  client_matrix_entry_not_found: "We couldn't find that client-matrix entry.",
  approved_client_matrix_entry_not_found: "We couldn't find that client-matrix entry.",
  adjudicator_cannot_decide_appeal: "You adjudicated this case, so you can't decide its appeal. A different reviewer has to.",
  reviewer_already_decided: "You've already recorded a decision here. A second, different reviewer has to record the next one.",
  actor_window_cap_exceeded: "Your approvals over the last 30 days would pass the 5,000 cap with this one. A different reviewer has to take it.",
  case_state_not_writable: "This case isn't in a state that allows this action right now.",
  provider_operation_not_unknown: "Only an unknown operation can take a manual outcome.",
  provider_operation_not_failed: "Only a finalized-failed operation can be corrected as a double payment.",
  write_off_exceeds_outstanding: "That's more than is outstanding on this case.",
  invalid_write_off_amount: "The write-off amount must be more than zero.",
  client_matrix_entry_exists: "That client and version are already in the matrix. Revalidate the existing entry instead.",
  // Migration 0069: reinstating (status:"validated") is refused through
  // /status outright — the console never sends this, but the code is
  // mapped for a stale client or a hand-built request.
  reinstatement_requires_dual_control: "Reinstating needs a second, distinct reviewer. Use the reinstate action below, not a plain status change.",
  provider_object_mismatch: "That Stripe object is linked to something else. Check the id and try again.",
  provider_object_already_attached: "That Stripe object already belongs to a different provider operation.",
  provider_object_changed_concurrently: "The attached Stripe object changed while this decision was in flight. Reload and try again.",
  nothing_to_acknowledge: "This operation has no flag on a finalized outcome to acknowledge.",
  provider_object_id_required: "A confirmed decision needs the Stripe object id.",
  invalid_provider_object_id: "That isn't a Stripe object id of the expected kind.",
  provider_object_required: "This operation has no attached provider object; a double-payment decision needs one.",
  attached_object_mismatch: "Name the object currently attached — it's the one that shows the money left.",
  not_a_double_payment: "There's no double payment or refund here to correct.",
  double_payment_needs_manual_correction: "This was already adjudicated; the correction needs to be built by hand. Contact engineering.",
  provider_operation_in_flight: "This operation is still in flight at the provider, or another decision on it is in progress. Try again shortly.",
  invalid_operation_type_for_double_payment: "Double payment only applies to payout and refund operations.",
  suspension_not_liftable: "This suspension couldn't be lifted. Check its current state.",
  missing_required_field: "A required field is missing.",
  internal_rejected: "The server refused this request.",
  // Safety findings, suspensions and the breaker (migration 0068).
  suspension_not_found: "We couldn't find that suspension.",
  not_a_safety_finding: "That suspension isn't a safety finding, so it can't be confirmed.",
  not_yet_confirmed: "This finding's fan-out hasn't started yet. A second reviewer has to confirm it first.",
  invalid_status: "That status filter isn't valid.",
  invalid_active: "That filter isn't valid.",
  invalid_state: "That state filter isn't valid.",
  invalid_suspension_id: "That isn't a valid suspension id.",
  // Reconciliation quarantines (migration 0072, reconciliation.go and
  // clear_financial_quarantine's _campaign_reject codes).
  quarantine_not_found: "We couldn't find that quarantine.",
  invalid_quarantine_id: "That isn't a valid quarantine id.",
  invalid_resolution: "Choose repaired or explained.",
  explanation_required: "Give an explanation (1–2,000 characters). It's recorded with your vote.",
  discrepancy_still_present:
    "A discrepancy is still present at the last reconciliation run, so this can't be cleared as repaired. Repair it first (the next run will see it), or clear it as explained.",
  resolution_mismatch: "The first reviewer chose a different resolution. Both votes have to agree on the same one.",
  quarantine_changed: "A reconciliation run changed this quarantine since you loaded it, so your vote wasn't recorded. Reload and review the new figures.",
  override_not_implemented: "The degraded-mode override isn't built yet. A quarantined account stays halted until two reviewers clear it.",
};

export function staffMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "request_rejected" && err.status === 400 && !err.detailText) {
      return "The server refused this without saying why.";
    }
    const m = err.code ? STAFF_CODE_MESSAGE[err.code] : undefined;
    return m ?? err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}

function detailFacts(err: ApiError): ReactNode[] {
  const d = err.detail ?? {};
  const s = (k: string) => err.detailString(k);
  const cur = s("currency") ?? "USD";
  const out: ReactNode[] = [];
  const list = (k: string) => (Array.isArray(d[k]) ? (d[k] as unknown[]).filter((x): x is string => typeof x === "string") : []);
  const regulated = list("regulated_categories");
  if (regulated.length) out.push(<span key="reg">Regulated categories to confirm: {regulated.join(", ")}.</span>);
  const cats = list("categories");
  if (cats.length) out.push(<span key="cats">Not cleared: {cats.join(", ")}.</span>);
  if (s("state") && err.code === "appeal_not_pending") out.push(<span key="st">The appeal is {s("state")}.</span>);
  if (s("outcome")) out.push(<span key="oc">It was {s("outcome")}.</span>);
  if (s("proposed")) out.push(<span key="prop">The first uphold proposed <Money value={s("proposed")!} currency={cur} />.</span>);
  if (s("available")) {
    out.push(
      <span key="avail">
        Publisher share <Money value={s("publisher_share") ?? "0"} currency={cur} />, already credited{" "}
        <Money value={s("already_credited") ?? "0"} currency={cur} />, available <Money value={s("available")!} currency={cur} />.
      </span>,
    );
  }
  if (s("claimed_amount")) out.push(<span key="cl">Claimed by a payout: <Money value={s("claimed_amount")!} currency={cur} />.</span>);
  if (s("cap")) {
    out.push(
      <span key="cap">
        Your approvals in the last {typeof d.window_days === "number" ? d.window_days : 30} days:{" "}
        <Money value={s("window_total") ?? "0"} currency={cur} />, this one{" "}
        <Money value={s("amount") ?? s("case_amount") ?? "0"} currency={cur} />, cap <Money value={s("cap")!} currency={cur} />.
      </span>,
    );
  }
  if (typeof d.state === "string" && err.code === "case_state_not_writable") out.push(<span key="csnw">The case is {s("state")!.replace(/_/g, " ")}.</span>);
  if (typeof d.status === "string" && (err.code === "provider_operation_not_unknown" || err.code === "provider_operation_not_failed")) {
    out.push(<span key="pons">The operation is {s("status")}.</span>);
  }
  if (typeof d.status === "string" && err.code === "slot_version_not_pending") out.push(<span key="svnp">This version is {s("status")}.</span>);
  if (s("reservation_state")) out.push(<span key="rs">The delivery is now {s("reservation_state")!.replace(/_/g, " ")}.</span>);
  if (typeof d.max_scale === "number") out.push(<span key="ms">At most {d.max_scale} decimal places.</span>);
  if (err.code === "discrepancy_still_present" && typeof d.present === "number") {
    out.push(<span key="dsp">Still present: {d.present} discrepancy(ies), current total {s("current_discrepancy") ?? "?"}.</span>);
  }
  if (err.code === "resolution_mismatch" && s("first_resolution")) out.push(<span key="rm">The first vote was “{s("first_resolution")}”.</span>);
  if (err.code === "quarantine_changed" && typeof d.current_version === "number") {
    out.push(<span key="qc">You saw version {String(d.expected_version)}; it is now version {d.current_version}.</span>);
  }
  return out;
}

/**
 * A refused or failed staff command: the staff wording, the facts the
 * typed `detail` carries, the server's own text for the 409
 * request_rejected answers (their detail is a string), a note for the
 * untyped 400s, and the reference id.
 */
export function StaffRejection({ error, untypedHint }: { error: unknown; untypedHint?: ReactNode }) {
  const api = error instanceof ApiError ? error : null;
  const facts = api ? detailFacts(api) : [];
  return (
    <Banner tone={api?.kind === "step_up_required" ? "neutral" : "error"} live="alert" title={staffMessage(error)}>
      <div className="stack-sm small">
        {api?.code ? (
          <span className="xs muted">
            Code <code>{api.code}</code>
            {api.status ? ` · HTTP ${api.status}` : null}
          </span>
        ) : null}
        {facts}
        {api?.detailText ? (
          <span>
            Server says: <q className="mono">{api.detailText}</q>
          </span>
        ) : null}
        {api?.code === "request_rejected" && api.status === 400 && untypedHint ? untypedHint : null}
        {api?.retryable ? (
          <span>We can't tell whether this reached the server. Check the current state before trying again.</span>
        ) : null}
        {api?.idempotencyKey ? <RefId value={api.idempotencyKey} /> : null}
      </div>
    </Banner>
  );
}

/**
 * An outcome that came back 200 with applied:false (review.go answers
 * every review with 200; set_approved_client_status's "unchanged").
 */
export function NotAppliedBanner({ reason, result, children }: { reason?: string; result: string; children?: ReactNode }) {
  const code = reason ?? result;
  return (
    <Banner tone="warning" live="polite" title={STAFF_CODE_MESSAGE[code] ?? `Not applied: ${code.replace(/_/g, " ")}`}>
      <div className="stack-sm small">
        <span className="xs muted">
          Outcome <code>{result}</code>
          {reason ? (
            <>
              {" "}
              · reason <code>{reason}</code>
            </>
          ) : null}
        </span>
        {children}
      </div>
    </Banner>
  );
}

// ---------------------------------------------------------------------------
// Step-up and dual control

/**
 * Says whether the server demands a fresh verification for this command.
 * `required` mirrors the route's RequireRecentMFA wrapper; where the
 * server doesn't (the action is still recorded against the reviewer),
 * the console says so rather than pretending.
 */
export function StepUpNote({ required }: { required: boolean }) {
  return (
    <p className="xs muted" data-testid="staff-step-up-note">
      {required
        ? "Needs a fresh verification (security key or authenticator code, valid 15 minutes). You'll be asked when you submit."
        : "The server doesn't ask for a fresh verification on this action today (see API-GAPS.md, staff console)."}
    </p>
  );
}

export interface Vote {
  actor_user_id: string;
  decision: string;
  decided_at: string;
}

/**
 * Two distinct reviewers (0036's discipline, reused by 0043/0050/0063):
 * how many of the required approvals are in, who cast them, and whether
 * the viewer is one of them (then a second, different reviewer has to
 * confirm; the server refuses the same reviewer twice).
 */
export function DualControlProgress({
  approvals,
  required,
  votes,
  label = "Approvals",
}: {
  approvals: number;
  required: number;
  votes: Vote[];
  label?: string;
}) {
  const { me } = useStaffSession();
  const mine = votes.some((v) => v.actor_user_id === me.user_id);
  const done = approvals >= required;
  return (
    <div className="staff-dual" role="group" aria-label="Dual control">
      <div className="row">
        <strong>
          {label}: {approvals} of {required}
        </strong>
        {done ? (
          <StatusPill tone="success">Complete</StatusPill>
        ) : required > 1 && approvals > 0 ? (
          <StatusPill tone="warning">Waiting for a second reviewer</StatusPill>
        ) : (
          <StatusPill tone="neutral">{required > 1 ? "Two distinct reviewers required" : "One reviewer"}</StatusPill>
        )}
      </div>
      {votes.length ? (
        <ol className="staff-votes">
          {votes.map((v) => (
            <li key={`${v.actor_user_id}-${v.decided_at}`}>
              <span className="mono">{v.actor_user_id === me.user_id ? "you" : shortId(v.actor_user_id)}</span> · {v.decision} ·{" "}
              {when(v.decided_at)}
            </li>
          ))}
        </ol>
      ) : null}
      {!done && mine ? (
        <Banner tone="info" title="A second reviewer must confirm">
          You've recorded your vote. A different platform reviewer has to confirm before anything is applied; the server refuses
          a second vote from you.
        </Banner>
      ) : null}
    </div>
  );
}

/** The answer to a vote that didn't complete the quorum: result awaiting_second_approval. */
export function AwaitingSecondBanner({ approvals, required }: { approvals?: number; required?: number }) {
  return (
    <Banner tone="info" live="polite" title="Recorded. A second reviewer must confirm">
      Your vote is in ({approvals ?? 1} of {required ?? 2}). Nothing has been applied yet: a different platform reviewer has to
      confirm it. There's no fallback: it waits until they do.
    </Banner>
  );
}

// ---------------------------------------------------------------------------
// Money confirmation

/**
 * The confirmation every money-moving decision passes through: the exact
 * amount (formatted, and the exact ledger string beside it), what it
 * does to which balances, and whether it applies now or waits for a
 * second reviewer. Nothing is sent until "Confirm".
 */
export function MoneyConfirmDialog({
  open,
  title,
  amount,
  currency,
  effect,
  pendingSecond,
  confirmLabel,
  busy,
  error,
  untypedHint,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  amount: string;
  currency: string | null;
  effect: ReactNode;
  /** This vote alone won't apply it: why a second reviewer must confirm. */
  pendingSecond?: string | false;
  confirmLabel: string;
  busy?: boolean;
  /** The command's refusal, shown inside the dialog so it isn't hidden behind it. */
  error?: unknown;
  /** Extra help under an untyped 400 request_rejected (the debt commands). */
  untypedHint?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      alert
      dismissible={!busy}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack-sm">
        <div className="staff-confirm-amount" data-testid="confirm-amount">
          <span className="stat-label">Amount</span>
          <span className="stat-value">
            <Amount value={amount} currency={currency} />
          </span>
          <span className="xs muted mono" data-testid="confirm-amount-exact">
            Exact: {amount} {currency?.toUpperCase() ?? ""}
          </span>
        </div>
        <div className="small">{effect}</div>
        {pendingSecond ? (
          <Banner tone="info" title="This vote alone doesn't apply it">
            {pendingSecond} A second, different reviewer has to confirm before any money moves.
          </Banner>
        ) : null}
        {error ? <StaffRejection error={error} untypedHint={untypedHint} /> : null}
      </div>
    </Dialog>
  );
}

/** A row of filter buttons (aria-pressed), for list screens. */
export function FilterTabs<V extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: V;
  options: Array<{ value: V; label: ReactNode }>;
  onChange: (v: V) => void;
}) {
  return (
    <div className="tabs" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="tab staff-tab" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Id lookup (areas with no list endpoint)

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function IdLookup({ label, onOpen, hint }: { label: string; onOpen: (id: string) => void; hint?: ReactNode }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  function submit(e: FormEvent) {
    e.preventDefault();
    const v = value.trim();
    if (!UUID_RE.test(v)) {
      setError("Enter the full id (a UUID).");
      return;
    }
    setError(null);
    onOpen(v.toLowerCase());
  }
  return (
    <form className="row staff-lookup" onSubmit={submit} noValidate>
      <Input label={label} mono value={value} onChange={(e) => setValue(e.target.value)} error={error ?? undefined} hint={hint} />
      <Button type="submit" variant="secondary">
        Open
      </Button>
    </form>
  );
}

export function isUuid(v: string): boolean {
  return UUID_RE.test(v.trim());
}

/**
 * One staff command as one user intent: the idempotency key is bound to
 * the request body (useIntentKey), so a retry, a double click or the
 * step-up interceptor's retry all reuse it, and it's reset once the
 * command succeeds. Only disputes.go:177 and receivable_write_off.go:164
 * replay under it today; the other internal routes ignore it.
 */
export function useStaffCommand<Req, Res>(send: (req: Req, opts: RequestOptions) => Promise<Res>, onDone?: (res: Res) => void) {
  const intent = useIntentKey();
  const m = useMutation({
    mutationFn: ({ req, sig }: { req: Req; sig: string }) => send(req, { idempotencyKey: intent.keyFor(sig) }),
    onSuccess: (res) => {
      intent.reset();
      onDone?.(res);
    },
  });
  return {
    run: (req: Req) => m.mutate({ req, sig: JSON.stringify(req) }),
    data: m.data,
    error: m.error,
    isPending: m.isPending,
    reset: m.reset,
  };
}

/**
 * Evidence typed as JSON: the commands take a JSON object (client_matrix.go
 * evidenceArg, provider_operations.go:195-198, debt_adjudication.go:124).
 */
export function parseEvidence(raw: string, { required }: { required: boolean }): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  const t = raw.trim();
  if (!t) return required ? { ok: false, error: "Evidence is required: a JSON object, like {\"ticket\": \"OPS-12\"}." } : { ok: true, value: {} };
  try {
    const v: unknown = JSON.parse(t);
    if (!v || typeof v !== "object" || Array.isArray(v)) return { ok: false, error: "Evidence must be a JSON object ({…})." };
    if (required && Object.keys(v).length === 0) return { ok: false, error: "Evidence can't be an empty object." };
    return { ok: true, value: v as Record<string, unknown> };
  } catch {
    return { ok: false, error: "That isn't valid JSON." };
  }
}

/** Reason: required, trimmed, at most 2,000 characters (0063/0061's limit, applied console-wide). */
export function reasonError(reason: string): string | null {
  const t = reason.trim();
  if (!t) return "Give a reason. It's recorded with your decision.";
  if (t.length > 2000) return "Keep the reason to 2,000 characters.";
  return null;
}
